// Procedural creature generator (v0.8.9 prototype).
//
// Each boss gets a parameter set derived from a stable hash of
// the boss name. The parameter set drives a hand-authored
// function that returns a THREE.Group with 800-2500 triangles.
//
// Render budget (Raspberry Pi 4, Chrome, kiosk mode):
//   - 1 ambient + 1 directional light (both static)
//   - MeshLambertMaterial only (no Phong / Standard / PBR)
//   - no shadows, no post-processing, no transparency stacks
//   - canvas: 128x128, embedded in the boss card
//
// Eight archetypes mirror the ASCII prototype so the stable hash
// picks the same creature regardless of engine:
//   - 'hunched': torso + head + 2 arms + horns/spikes
//   - 'tentacle': body + 8 waving tentacles
//   - 'eye':     single hovering sphere with pupil + lashes
//   - 'swarm':   cluster of small floating cubes + 2 cores
//   - 'beast':   4-legged body + head + tail + ears
//   - 'armored': chunky body + shield + spikes on shoulders
//   - 'dragon':  4-legged body + 2 wings + head + tail (new)
//   - 'golem':   rocky humanoid with cracks + glowing core (new)
//
// v0.8.9 (this version): every archetype gets accessory detail
// (horns, spikes, ears, tails, wings). Per-boss variation is
// seeded by the same hash so back-to-back bosses with the same
// archetype still look different.

import * as THREE from 'three';

export type CreatureId = SpriteIdFor3D;

type SpriteIdFor3D =
  | 'hunched'
  | 'tentacle'
  | 'eye'
  | 'swarm'
  | 'beast'
  | 'armored'
  | 'dragon'
  | 'golem';

export interface CreatureParams {
  id: CreatureId;
  // 0xRRGGBB integer; lambert material colour.
  bodyColor: number;
  // 0xRRGGBB integer; eye/contrast color.
  eyeColor: number;
  // size multiplier, 0.85 - 1.15
  scale: number;
  // float in [-0.4, 0.4], applied as body offset for variety.
  yOffset: number;
  // Number of horns (hunched/armored/golem): 0-3.
  hornCount: number;
  // Number of spikes on back/beast tail: 0-5.
  spikeCount: number;
  // Wing tilt in radians (dragon only): -0.3 to 0.3.
  wingTilt: number;
}

// Same FNV-1a hash as the ASCII prototype (sprites.ts).
function stableHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Seeded PRNG so the same input gives the same output every render.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Curated palette of "monster" colours. Each entry is a body
// colour; the eye colour is the highest-contrast pair from the
// same row.
const PALETTE: Array<[number, number]> = [
  [0x8b1a1a, 0xffd86b], // dark crimson + amber
  [0x4a1a6b, 0xc4f0ff], // bruise purple + ice
  [0x1a4a2a, 0xb6ff9c], // moss + lime
  [0x6b4a1a, 0xffe6a8], // ochre + cream
  [0x2a2a3a, 0xff6b6b], // slate + coral
  [0x5a1a1a, 0xa8ff8a], // maroon + mint
  [0x3a2a1a, 0xff9c6b], // umber + peach
  [0x1a3a4a, 0xc4f0ff], // deep teal + ice
];

const ARCHETYPES: CreatureId[] = [
  'hunched', 'tentacle', 'eye', 'swarm', 'beast', 'armored', 'dragon', 'golem',
];

export function paramsFor(bossName: string): CreatureParams {
  const seed = stableHash(bossName);
  const rng = mulberry32(seed);
  const id = ARCHETYPES[seed % ARCHETYPES.length]!;
  const palette = PALETTE[Math.floor(rng() * PALETTE.length)]!;
  return {
    id,
    bodyColor: palette[0]!,
    eyeColor: palette[1]!,
    scale: 0.85 + rng() * 0.30,
    yOffset: (rng() - 0.5) * 0.8,
    hornCount: Math.floor(rng() * 4),     // 0-3
    spikeCount: Math.floor(rng() * 6),    // 0-5
    wingTilt: (rng() - 0.5) * 0.6,        // -0.3 to 0.3
  };
}

// ---- archetype builders ----

// Add a cone horn to a creature. Returns the mesh.
function makeHorn(color: number, length: number, radius: number): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.ConeGeometry(radius, length, 8),
    new THREE.MeshLambertMaterial({ color, flatShading: true }),
  );
}

// Add a row of small spike pyramids along a body axis.
function makeSpikes(
  color: number,
  count: number,
  axisLength: number,
  baseOffset: THREE.Vector3,
  direction: 'up' | 'back',
): THREE.Mesh[] {
  const out: THREE.Mesh[] = [];
  for (let i = 0; i < count; i++) {
    const spike = new THREE.Mesh(
      new THREE.ConeGeometry(0.07, 0.18, 8),
      new THREE.MeshLambertMaterial({ color, flatShading: true }),
    );
    const t = (i + 0.5) / count;
    if (direction === 'up') {
      spike.position.set(
        baseOffset.x + (t - 0.5) * axisLength,
        baseOffset.y + 0.3,
        baseOffset.z,
      );
    } else {
      spike.position.set(
        baseOffset.x - 0.3,
        baseOffset.y + 0.2,
        baseOffset.z + (t - 0.5) * axisLength,
      );
    }
    spike.rotation.x = direction === 'up' ? 0 : Math.PI / 2;
    out.push(spike);
  }
  return out;
}

function buildHunched(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  // Body (lower-poly icosahedron, level 2 for more facets)
  const body = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.9, 3),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  body.position.y = -0.2 + p.yOffset * 0.3;
  g.add(body);
  // Head
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.55, 22, 16),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  head.position.set(0, 0.5 + p.yOffset * 0.3, 0.1);
  g.add(head);
  // Eyes
  for (const x of [-0.18, 0.18]) {
    const eye = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 10, 8),
      new THREE.MeshLambertMaterial({ color: p.eyeColor }),
    );
    eye.position.set(x, 0.55 + p.yOffset * 0.3, 0.5);
    g.add(eye);
  }
  // Horns — count from p.hornCount. Symmetric pair on the head.
  for (let i = 0; i < p.hornCount; i++) {
    for (const x of [-1, 1]) {
      const horn = makeHorn(p.bodyColor, 0.35, 0.08);
      horn.position.set(x * 0.18, 0.85 + p.yOffset * 0.3 + i * 0.05, 0.05);
      horn.rotation.z = x * (0.4 + i * 0.15);
      horn.rotation.x = -0.2;
      g.add(horn);
    }
  }
  // Shoulders — two small bumps (give the silhouette some shape)
  for (const x of [-0.55, 0.55]) {
    const shoulder = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 14, 10),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    shoulder.position.set(x, 0.15 + p.yOffset * 0.3, 0);
    g.add(shoulder);
  }
  // Arms
  for (const x of [-0.7, 0.7]) {
    const arm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.12, 0.7, 12),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    arm.position.set(x, -0.1 + p.yOffset * 0.3, 0);
    arm.rotation.z = x < 0 ? 0.5 : -0.5;
    g.add(arm);
    // Hands (small spheres at the end of the arm)
    const hand = new THREE.Mesh(
      new THREE.SphereGeometry(0.14, 14, 10),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    hand.position.set(x * 0.95, -0.5 + p.yOffset * 0.3, 0.05);
    g.add(hand);
    // Claws — three dark cones on each hand
    for (let i = -1; i <= 1; i++) {
      const claw = new THREE.Mesh(
        new THREE.ConeGeometry(0.022, 0.12, 8),
        new THREE.MeshLambertMaterial({ color: 0x111111, flatShading: true }),
      );
      claw.position.set(x * 0.95 + i * 0.04, -0.62 + p.yOffset * 0.3, 0.08);
      claw.rotation.z = x * -0.4;
      g.add(claw);
    }
  }
  // Mouth opening — thin dark box across the head
  const mouth = new THREE.Mesh(
    new THREE.BoxGeometry(0.32, 0.06, 0.05),
    new THREE.MeshLambertMaterial({ color: 0x111111 }),
  );
  mouth.position.set(0, 0.32 + p.yOffset * 0.3, 0.5);
  g.add(mouth);
  // Teeth — small white cones protruding down from the mouth
  for (let i = -2; i <= 2; i++) {
    const tooth = new THREE.Mesh(
      new THREE.ConeGeometry(0.022, 0.10, 8),
      new THREE.MeshLambertMaterial({ color: 0xeeeeee }),
    );
    tooth.position.set(i * 0.06, 0.27 + p.yOffset * 0.3, 0.52);
    tooth.rotation.x = Math.PI;
    g.add(tooth);
  }
  g.scale.setScalar(p.scale);
  return g;
}

function buildTentacle(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  // Body bulb (level-2 icosahedron — more facets for the rounded look)
  const body = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.55, 2),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  body.position.set(0, 0.3 + p.yOffset * 0.3, 0);
  g.add(body);
  // Eye on top of the bulb
  const eye = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 20, 16),
    new THREE.MeshLambertMaterial({ color: p.eyeColor, emissive: p.eyeColor, emissiveIntensity: 0.2 }),
  );
  eye.position.set(0, 0.6 + p.yOffset * 0.3, 0.32);
  g.add(eye);
  // Pupil — tiny black dot
  const pupil = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 10, 8),
    new THREE.MeshLambertMaterial({ color: 0x000000 }),
  );
  pupil.position.set(0, 0.62 + p.yOffset * 0.3, 0.48);
  g.add(pupil);
  // 8 tentacles (was 6) — fanned out around the body, with
  // a slight curve for personality.
  const tentCount = 8;
  for (let i = 0; i < tentCount; i++) {
    const angle = (i / tentCount) * Math.PI * 2;
    const length = 0.85 + (i % 2 === 0 ? 0.15 : -0.1);
    const tent = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.14, length, 10),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    tent.position.set(
      Math.cos(angle) * 0.45,
      -0.4 + p.yOffset * 0.3 + (i % 2 === 0 ? 0 : 0.05),
      Math.sin(angle) * 0.45,
    );
    tent.rotation.z = Math.cos(angle) * 0.7;
    tent.rotation.x = Math.sin(angle) * 0.7;
    g.add(tent);
    // Tip (small sphere to soften the cylinder end)
    const tip = new THREE.Mesh(
      new THREE.SphereGeometry(0.07, 10, 8),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    tip.position.set(
      tent.position.x + Math.cos(angle) * length * 0.5,
      tent.position.y + Math.sin(angle) * length * 0.5,
      tent.position.z + Math.sin(angle) * length * 0.5,
    );
    g.add(tip);
  }
  g.scale.setScalar(p.scale);
  return g;
}

function buildEye(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  // Outer eye white (level-2 icosahedron — smoother dome)
  const outer = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.85, 3),
    new THREE.MeshLambertMaterial({ color: 0xeee5d4, flatShading: true }),
  );
  outer.position.set(0, p.yOffset * 0.3, 0);
  g.add(outer);
  // Iris
  const iris = new THREE.Mesh(
    new THREE.SphereGeometry(0.55, 24, 18),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  iris.position.set(0, p.yOffset * 0.3, 0.55);
  g.add(iris);
  // Pupil
  const pupil = new THREE.Mesh(
    new THREE.SphereGeometry(0.25, 22, 16),
    new THREE.MeshLambertMaterial({ color: 0x000000 }),
  );
  pupil.position.set(0, p.yOffset * 0.3, 0.88);
  g.add(pupil);
  // Highlight catch-light
  const catch1 = new THREE.Mesh(
    new THREE.SphereGeometry(0.10, 10, 8),
    new THREE.MeshLambertMaterial({ color: 0xffffff }),
  );
  catch1.position.set(0.22, 0.22 + p.yOffset * 0.3, 1.0);
  g.add(catch1);
  // Lashes — three small cones above the eye to give it
  // character. Reads as "an angry eye" rather than "a sphere".
  for (let i = -1; i <= 1; i++) {
    const lash = new THREE.Mesh(
      new THREE.ConeGeometry(0.05, 0.18, 8),
      new THREE.MeshLambertMaterial({ color: 0xeee5d4, flatShading: true }),
    );
    lash.position.set(i * 0.18, 0.6 + p.yOffset * 0.3, 0.3);
    lash.rotation.x = Math.PI; // point outward
    lash.rotation.z = i * 0.4;
    g.add(lash);
  }
  g.scale.setScalar(p.scale);
  return g;
}

function buildSwarm(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  const rng = mulberry32(stableHash('swarm-' + p.bodyColor));
  // 18 cubes (was 14). Spread further for visual depth.
  for (let i = 0; i < 18; i++) {
    const size = 0.10 + rng() * 0.20;
    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(size, size, size),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    const theta = rng() * Math.PI * 2;
    const phi = (rng() - 0.5) * Math.PI;
    const r = 0.4 + rng() * 0.7;
    cube.position.set(
      Math.cos(theta) * Math.cos(phi) * r,
      Math.sin(phi) * r + p.yOffset * 0.3,
      Math.sin(theta) * Math.cos(phi) * r,
    );
    cube.rotation.set(rng() * Math.PI, rng() * Math.PI, rng() * Math.PI);
    g.add(cube);
  }
  // Two glowing cores (was 1) — small + larger, gives the
  // swarm a sense of "there's a thing in there".
  for (const size of [0.14, 0.22]) {
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(size, 18, 14),
      new THREE.MeshLambertMaterial({
        color: p.eyeColor,
        emissive: p.eyeColor,
        emissiveIntensity: 0.5,
      }),
    );
    core.position.set(0, p.yOffset * 0.3, 0);
    g.add(core);
  }
  g.scale.setScalar(p.scale);
  return g;
}

function buildBeast(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  // Body (elongated)
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.4, 0.6, 0.8),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  body.position.set(0, -0.1 + p.yOffset * 0.3, 0);
  g.add(body);
  // Head
  const head = new THREE.Mesh(
    new THREE.BoxGeometry(0.6, 0.5, 0.55),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  head.position.set(0.85, 0.1 + p.yOffset * 0.3, 0);
  g.add(head);
  // Snout
  const snout = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.3, 0.4),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  snout.position.set(1.2, -0.05 + p.yOffset * 0.3, 0);
  g.add(snout);
  // Ears — two small cones on top of the head
  for (const x of [-0.15, 0.15]) {
    const ear = new THREE.Mesh(
      new THREE.ConeGeometry(0.1, 0.22, 8),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    ear.position.set(0.7 + x, 0.45 + p.yOffset * 0.3, 0);
    ear.rotation.z = x * -0.3;
    g.add(ear);
  }
  // Eyes
  for (const y of [0.2, 0.2]) {
    for (const z of [-0.15, 0.15]) {
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 10, 8),
        new THREE.MeshLambertMaterial({ color: p.eyeColor }),
      );
      eye.position.set(0.95, 0.30 + p.yOffset * 0.3, z);
      g.add(eye);
    }
  }
  // 4 legs (cylinder level 8 for smoother silhouette)
  for (const x of [-0.5, 0.5]) {
    for (const z of [-0.3, 0.3]) {
      const leg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.08, 0.5, 12),
        new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
      );
      leg.position.set(x, -0.55 + p.yOffset * 0.3, z);
      g.add(leg);
      // Paw (small box)
      const paw = new THREE.Mesh(
        new THREE.BoxGeometry(0.14, 0.08, 0.14),
        new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
      );
      paw.position.set(x, -0.82 + p.yOffset * 0.3, z);
      g.add(paw);
      // Claws — three small dark cones on each paw
      for (let i = -1; i <= 1; i++) {
        const claw = new THREE.Mesh(
          new THREE.ConeGeometry(0.022, 0.12, 8),
          new THREE.MeshLambertMaterial({ color: 0x111111, flatShading: true }),
        );
        claw.position.set(x, -0.90 + p.yOffset * 0.3, z + i * 0.04);
        g.add(claw);
      }
    }
  }
  // Tail — short cylinder extending from the rear, with
  // spike pyramids along it (count from p.spikeCount).
  const tail = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.18, 0.7, 10),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  tail.position.set(-0.85, 0 + p.yOffset * 0.3, 0);
  tail.rotation.z = Math.PI / 2;
  g.add(tail);
  for (const spike of makeSpikes(p.bodyColor, p.spikeCount, 0.4, new THREE.Vector3(-1.1, 0, 0), 'back')) {
    g.add(spike);
  }
  // Mane — small cones along the spine
  for (let i = 0; i < 4; i++) {
    const mane = new THREE.Mesh(
      new THREE.ConeGeometry(0.06, 0.16, 8),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    mane.position.set(0.4 - i * 0.4, 0.4 + p.yOffset * 0.3, 0);
    g.add(mane);
  }
  g.scale.setScalar(p.scale);
  return g;
}

function buildArmored(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  // Body
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.9, 1.1, 0.7),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  body.position.set(0, -0.2 + p.yOffset * 0.3, 0);
  g.add(body);
  // Head
  const head = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.5, 0.5),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  head.position.set(0, 0.55 + p.yOffset * 0.3, 0);
  g.add(head);
  // Visor (eye line) — glowing
  const visor = new THREE.Mesh(
    new THREE.BoxGeometry(0.4, 0.1, 0.05),
    new THREE.MeshLambertMaterial({
      color: p.eyeColor,
      emissive: p.eyeColor,
      emissiveIntensity: 0.3,
    }),
  );
  visor.position.set(0, 0.55 + p.yOffset * 0.3, 0.28);
  g.add(visor);
  // Shield — a thicker rectangle with a gold rim
  const shield = new THREE.Mesh(
    new THREE.BoxGeometry(0.6, 0.8, 0.08),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  shield.position.set(0, -0.2 + p.yOffset * 0.3, 0.55);
  g.add(shield);
  // Shield rim — small box around the edge to read as "armor"
  const shieldRim = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 0.05, 0.1),
    new THREE.MeshLambertMaterial({ color: p.eyeColor, flatShading: true }),
  );
  shieldRim.position.set(0, 0.2 + p.yOffset * 0.3, 0.55);
  g.add(shieldRim);
  const shieldRimBot = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 0.05, 0.1),
    new THREE.MeshLambertMaterial({ color: p.eyeColor, flatShading: true }),
  );
  shieldRimBot.position.set(0, -0.6 + p.yOffset * 0.3, 0.55);
  g.add(shieldRimBot);
  // Shoulder spikes — count from p.spikeCount
  for (let i = 0; i < p.spikeCount; i++) {
    const x = i % 2 === 0 ? -0.55 : 0.55;
    const spike = new THREE.Mesh(
      new THREE.ConeGeometry(0.08, 0.22, 8),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    spike.position.set(x, 0.55 + p.yOffset * 0.3, 0);
    spike.rotation.z = x < 0 ? -0.3 : 0.3;
    g.add(spike);
  }
  // Arms
  for (const x of [-0.55, 0.55]) {
    const arm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.16, 0.16, 0.7, 12),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    arm.position.set(x, -0.4 + p.yOffset * 0.3, 0.2);
    arm.rotation.x = -0.3;
    g.add(arm);
    // Gauntlet (small cube on the fist)
    const gauntlet = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, 0.18, 0.22),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    gauntlet.position.set(x, -0.78 + p.yOffset * 0.3, 0.35);
    g.add(gauntlet);
  }
  g.scale.setScalar(p.scale);
  return g;
}

function buildDragon(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  // Body
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.5, 0.55, 0.7),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  body.position.set(0, 0 + p.yOffset * 0.3, 0);
  g.add(body);
  // Neck — angled cylinder
  const neck = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.22, 0.6, 12),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  neck.position.set(0.85, 0.3 + p.yOffset * 0.3, 0);
  neck.rotation.z = -0.6;
  g.add(neck);
  // Head
  const head = new THREE.Mesh(
    new THREE.BoxGeometry(0.45, 0.35, 0.4),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  head.position.set(1.2, 0.55 + p.yOffset * 0.3, 0);
  g.add(head);
  // Snout
  const snout = new THREE.Mesh(
    new THREE.BoxGeometry(0.25, 0.2, 0.3),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  snout.position.set(1.45, 0.45 + p.yOffset * 0.3, 0);
  g.add(snout);
  // Eyes (glowing)
  for (const z of [-0.12, 0.12]) {
    const eye = new THREE.Mesh(
      new THREE.SphereGeometry(0.06, 10, 8),
      new THREE.MeshLambertMaterial({
        color: p.eyeColor,
        emissive: p.eyeColor,
        emissiveIntensity: 0.4,
      }),
    );
    eye.position.set(1.30, 0.65 + p.yOffset * 0.3, z);
    g.add(eye);
  }
  // Horns — count from p.hornCount
  for (let i = 0; i < Math.min(2, p.hornCount); i++) {
    for (const x of [-0.12, 0.12]) {
      const horn = makeHorn(p.bodyColor, 0.30, 0.06);
      horn.position.set(1.15 + x, 0.85 + p.yOffset * 0.3 + i * 0.05, 0);
      horn.rotation.z = x * (0.4 + i * 0.1);
      g.add(horn);
    }
  }
  // Wings — two planes (BoxGeometry flattened). Spread out
  // from the body sides. wingTilt rotates each wing a bit
  // so different bosses don't all look identical.
  for (const x of [-1, 1]) {
    const wing = new THREE.Mesh(
      new THREE.BoxGeometry(0.9, 0.5, 0.05),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    wing.position.set(x * 0.2, 0.4 + p.yOffset * 0.3, x * 0.55);
    wing.rotation.x = p.wingTilt;
    wing.rotation.y = x * 0.6;
    g.add(wing);
    // Wing fingers — small cones for the membrane detail
    for (let i = 0; i < 3; i++) {
      const finger = new THREE.Mesh(
        new THREE.ConeGeometry(0.05, 0.3, 8),
        new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
      );
      finger.position.set(
        x * (0.4 + i * 0.25),
        0.4 + p.yOffset * 0.3 + (i % 2 === 0 ? 0 : -0.15),
        x * 0.9,
      );
      finger.rotation.x = p.wingTilt + Math.PI / 2;
      finger.rotation.y = x * 0.6;
      g.add(finger);
    }
  }
  // Tail — segmented cylinder, angled down
  const tail = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.25, 1.0, 12),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  tail.position.set(-0.95, -0.2 + p.yOffset * 0.3, 0);
  tail.rotation.z = Math.PI / 2 + 0.3;
  g.add(tail);
  // Tail spikes (count from p.spikeCount)
  for (const spike of makeSpikes(p.bodyColor, p.spikeCount, 0.7, new THREE.Vector3(-1.4, -0.2, 0), 'back')) {
    g.add(spike);
  }
  // 4 legs (smaller than beast)
  for (const x of [-0.5, 0.5]) {
    for (const z of [-0.25, 0.25]) {
      const leg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.07, 0.4, 10),
        new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
      );
      leg.position.set(x, -0.4 + p.yOffset * 0.3, z);
      g.add(leg);
      // Claws — three small cones on each foot, fanned out
      for (let i = -1; i <= 1; i++) {
        const claw = new THREE.Mesh(
          new THREE.ConeGeometry(0.025, 0.14, 8),
          new THREE.MeshLambertMaterial({ color: 0x111111, flatShading: true }),
        );
        claw.position.set(x + i * 0.05, -0.62 + p.yOffset * 0.3, z);
        claw.rotation.z = i * 0.3;
        g.add(claw);
      }
    }
  }
  g.scale.setScalar(p.scale);
  return g;
}

function buildGolem(p: CreatureParams): THREE.Group {
  const g = new THREE.Group();
  // Body — chunky low-poly rock shape (icosahedron level 1 for the chunky look)
  const body = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.95, 3),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  body.position.y = -0.15 + p.yOffset * 0.3;
  body.scale.set(1.0, 1.1, 0.9);
  g.add(body);
  // Head (smaller chunky rock)
  const head = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.55, 2),
    new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
  );
  head.position.set(0, 0.55 + p.yOffset * 0.3, 0.1);
  g.add(head);
  // Glowing cracks — thin emissive boxes scattered across the body.
  // Two cracks per count (symmetric, give the golem an "energy
  // leaking from inside" feel).
  for (let i = 0; i < Math.min(4, p.hornCount + 1); i++) {
    const crack = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.04, 0.05),
      new THREE.MeshLambertMaterial({
        color: p.eyeColor,
        emissive: p.eyeColor,
        emissiveIntensity: 0.6,
      }),
    );
    const angle = (i / 4) * Math.PI * 2;
    crack.position.set(
      Math.cos(angle) * 0.6,
      -0.1 + p.yOffset * 0.3 + (i % 2 === 0 ? 0.2 : -0.2),
      Math.sin(angle) * 0.6,
    );
    crack.rotation.z = angle;
    g.add(crack);
  }
  // Eyes (glowing)
  for (const x of [-0.18, 0.18]) {
    const eye = new THREE.Mesh(
      new THREE.SphereGeometry(0.10, 10, 8),
      new THREE.MeshLambertMaterial({
        color: p.eyeColor,
        emissive: p.eyeColor,
        emissiveIntensity: 0.5,
      }),
    );
    eye.position.set(x, 0.6 + p.yOffset * 0.3, 0.45);
    g.add(eye);
  }
  // Arms — chunky cylinders
  for (const x of [-0.75, 0.75]) {
    const arm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22, 0.18, 0.8, 10),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    arm.position.set(x, -0.15 + p.yOffset * 0.3, 0);
    arm.rotation.z = x < 0 ? 0.4 : -0.4;
    g.add(arm);
    // Fists
    const fist = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.22, 2),
      new THREE.MeshLambertMaterial({ color: p.bodyColor, flatShading: true }),
    );
    fist.position.set(x * 1.05, -0.55 + p.yOffset * 0.3, 0);
    g.add(fist);
  }
  // Horns (count from p.hornCount)
  for (let i = 0; i < p.hornCount; i++) {
    for (const x of [-1, 1]) {
      const horn = makeHorn(p.bodyColor, 0.40, 0.10);
      horn.position.set(x * 0.20, 0.85 + p.yOffset * 0.3 + i * 0.06, 0.05);
      horn.rotation.z = x * (0.4 + i * 0.15);
      horn.rotation.x = -0.15;
      g.add(horn);
    }
  }
  g.scale.setScalar(p.scale);
  return g;
}

const BUILDERS: Record<CreatureId, (p: CreatureParams) => THREE.Group> = {
  hunched: buildHunched,
  tentacle: buildTentacle,
  eye: buildEye,
  swarm: buildSwarm,
  beast: buildBeast,
  armored: buildArmored,
  dragon: buildDragon,
  golem: buildGolem,
};

export function buildCreature(params: CreatureParams): THREE.Group {
  return BUILDERS[params.id](params);
}
